import { Box, Popover, Toolbar } from "@mui/material"
import React, { useEffect, useState } from "react"
import PlacementDetailsContainerView from "../placement/PlacementDetailsContainerView"
import PlacementListView from "../placements/PlacementListView"
import ProjectLoadingContainer from "../projects/ProjectLoadingContainer"
import EmptyIconView from "../reusable/EmptyIconView"
import NavigationBarView from "../reusable/NavigationBarView"
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import ProjectSettingsView from "../projects/ProjectSettingsView"
import { useAppSelector } from "../../store/hooks"
import NotFound from "./NotFound"

// Wrapper component to check if project exists
const ProjectRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const { projectId } = useParams()
  const loadedProjects = useAppSelector(state => state.project.projects)

  if (!projectId) {
    return <NotFound />
  }

  const project = loadedProjects.find(p => p.id === parseInt(projectId))
  if (!project) {
    return <NotFound />
  }

  return <>{children}</>
}

// Wrapper component to check if placement exists
const PlacementRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const { projectId, placementId } = useParams()
  const loadedProjects = useAppSelector(state => state.project.projects)

  if (!projectId || !placementId) {
    return <NotFound />
  }

  const project = loadedProjects.find(p => p.id === parseInt(projectId))
  if (!project) {
    return <NotFound />
  }

  const placement = project.placements?.find(p => p.id === parseInt(placementId))
  if (!placement) {
    return <NotFound />
  }

  return <>{children}</>
}

const TemperatureAndMaturityMonitoringLandingView = () => {
  const [projectSettings, setProjectSettings] = useState<HTMLElement | null>(null)
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined)
  const loadedProjects = useAppSelector(state => state.project.projects)
  let selectedProject = undefined
  if (selectedProjectId !== undefined) {
    selectedProject = loadedProjects.find(p => p.id === parseInt(selectedProjectId))
  }

  const togglePopover = (event: React.MouseEvent<HTMLElement>) => {
    setProjectSettings(event.currentTarget)
  }

  const popoverIsOpen = Boolean(projectSettings)
  const popoverId = popoverIsOpen ? "placement-specs" : undefined

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const projectId = (location.pathname.split("/projects/")[1] || undefined)
    setSelectedProjectId(projectId)
  }, [location])

  // Check if the current path is a valid route
  const isValidRoute = location.pathname === "/" ||
    /^\/projects\/\d+$/.test(location.pathname) ||
    /^\/projects\/\d+\/placements\/\d+$/.test(location.pathname)

  // Check if project/placement actually exists
  const projectIdMatch = location.pathname.match(/^\/projects\/(\d+)/)
  const placementIdMatch = location.pathname.match(/^\/projects\/\d+\/placements\/(\d+)/)

  let projectExists = true
  let placementExists = true

  if (projectIdMatch) {
    const projectId = parseInt(projectIdMatch[1])
    const project = loadedProjects.find(p => p.id === projectId)
    projectExists = project !== undefined

    if (placementIdMatch && projectExists) {
      const placementId = parseInt(placementIdMatch[1])
      const placement = project?.placements?.find(p => p.id === placementId)
      placementExists = placement !== undefined
    }
  }

  const resourceExists = isValidRoute && (location.pathname === "/" || (projectExists && placementExists))

  return (
    <Box display="flex" flexDirection="column" alignItems="stetch" sx={{
      height: "100%",
      overflow: "hidden"
    }}>
      <Toolbar />
      <NavigationBarView
        title={selectedProject?.name || "COMMAND Center Concrete"}
        backButtonTitle={(selectedProjectId || !resourceExists) ? "All Projects" : undefined}
        onBack={() => {
          navigate("/")
        }}
        projectSettingsButtonTitle={selectedProjectId && resourceExists ? "Settings" : undefined}
        onProjectSettingsClicked={togglePopover}
      />
      <Box display="flex" sx={{
        overflow: "hidden",
        height: "100%"
      }} flexDirection="row" alignItems="stretch" position="relative" flexGrow={1}>
        {resourceExists && (
          <Box flexGrow={1} flexShrink={0} sx={{
            height: "100%",
            overflow: "hidden",
            maxWidth: "336px",
            minWidth: "336px",
            width: "336px",
            borderRight: "1px solid gray"
          }}>
            <Routes>
              <Route path="/" element={
                <ProjectLoadingContainer
                  onProjectSelect={(project) => {
                    navigate(`/projects/${project.id}`)
                  }} />
              } />
              <Route path="/projects/:projectId" element={
                <PlacementListView
                  onPlacementSelect={(placement) => {
                    navigate(`/projects/${placement.projectId}/placements/${placement.id}`)
                  }}
                />
              } />
              <Route path="/projects/:projectId/placements/:placementId" element={
                <PlacementListView
                  onPlacementSelect={(placement) => {
                    navigate(`/projects/${placement.projectId}/placements/${placement.id}`)
                  }}
                />
              } />
            </Routes>
          </Box>
        )}
        <Box display="flex" sx={{
          overflow: "hidden"
        }} flexGrow={4}>
          <Routes>
            <Route path="/" element={
              <EmptyIconView helperText="Select a project from the menu on the left to see it here." />
            } />
            <Route path="/projects/:projectId" element={
              <ProjectRouteWrapper>
                <EmptyIconView helperText="Select a placement from the menu on the left to see it here." />
              </ProjectRouteWrapper>
            } />
            <Route path="/projects/:projectId/placements/:placementId" element={
              <PlacementRouteWrapper>
                <PlacementDetailsContainerView />
              </PlacementRouteWrapper>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </Box>
      {selectedProject &&
        <Popover
          id={popoverId}
          open={popoverIsOpen}
          anchorEl={projectSettings}
          onClose={() => { setProjectSettings(null) }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <Box sx={{
            width: "500px"
          }}>
            <ProjectSettingsView project={selectedProject} />
          </Box>
        </Popover>
      }
    </Box>
  )
}

export default TemperatureAndMaturityMonitoringLandingView
